/**
 * GuestBanner — fixed bottom CTA that replaces the BottomNav for guest
 * users on /nutrition, /trainers, and the scanner landing.
 *
 * Iter 52.1: added a "locked preview" of the 5-tab member navigation
 * ABOVE the sign-up CTA. Icons are shown at ~55% opacity to signal
 * they're not yet reachable, but they're tap-able — each fires the
 * same `onSignUp` handler so the login / create-account sheet opens.
 * The intent is curiosity: give the guest a peek at what's behind the
 * wall instead of a blank strip.
 */
import { Home, Dumbbell, Apple, Users, ShoppingBag, Lock } from "lucide-react";

const LOCKED_TABS = [
  { key: "home", label: "Home", icon: Home },
  { key: "workouts", label: "Workouts", icon: Dumbbell },
  { key: "nutrition", label: "Nutrition", icon: Apple },
  { key: "community", label: "Community", icon: Users },
  { key: "shop", label: "Shop", icon: ShoppingBag },
];

export default function GuestBanner({ onSignUp }) {
  return (
    <div
      data-testid="guest-banner"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 50,
        background: "rgba(255,255,255,0.85)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderTop: "1px solid #E2E0DC",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* ── Locked-preview nav row (Iter 52.1) ── */}
      <div
        data-testid="guest-nav-preview"
        role="group"
        aria-label="Sign up to unlock these features"
        style={{
          display: "flex",
          alignItems: "stretch",
          justifyContent: "space-between",
          maxWidth: 672,
          margin: "0 auto",
          height: 60,
          padding: "0 4px",
          borderBottom: "1px solid rgba(226,224,220,0.6)",
          position: "relative",
        }}
      >
        {LOCKED_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={onSignUp}
            data-testid={`guest-nav-locked-${t.key}`}
            aria-label={`${t.label} — sign up to unlock`}
            style={{
              flex: 1,
              background: "transparent",
              border: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              cursor: "pointer",
              opacity: 0.55,
              transition: "opacity 160ms ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.55"; }}
          >
            <t.icon size={20} strokeWidth={1.8} color="#4A4845" />
            <span
              style={{
                fontFamily: "Manrope",
                fontSize: 10.5,
                fontWeight: 500,
                color: "#4A4845",
                letterSpacing: "0.01em",
              }}
            >
              {t.label}
            </span>
          </button>
        ))}
        {/* Tiny lock badge in the top-right corner so the intent is
            unambiguous — this row is a preview, not the real nav. */}
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 6,
            right: 8,
            width: 18,
            height: 18,
            borderRadius: 999,
            background: "rgba(15,15,16,0.06)",
            display: "grid",
            placeItems: "center",
          }}
        >
          <Lock size={10} color="#6B6760" strokeWidth={2.2} />
        </span>
      </div>

      {/* ── Sign-up CTA row (unchanged copy + button) ── */}
      <div
        style={{
          height: 64,
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div
          style={{
            flex: 1,
            fontFamily: "Manrope",
            fontSize: 13,
            fontWeight: 500,
            color: "#6B6760",
            lineHeight: 1.25,
          }}
        >
          Sign up free to access all features
        </div>
        <button
          onClick={onSignUp}
          data-testid="guest-banner-signup"
          style={{
            height: 40,
            padding: "0 20px",
            borderRadius: 100,
            background: "#00D4FF",
            color: "#0B0B0B",
            fontFamily: "Manrope",
            fontWeight: 700,
            fontSize: 13,
            flexShrink: 0,
          }}
        >
          Sign Up
        </button>
      </div>
    </div>
  );
}
